import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled, { keyframes } from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconChevronDown } from '@sorare/core/src/atoms/icons/system/iconChevronDown.ts';
import { iconChevronUp } from '@sorare/core/src/atoms/icons/system/iconChevronUp.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import { SeasonalityLabel } from '#src/components/lineup/SeasonalityLabel/index.tsx';
import { CardLayout } from '#src/components/so5/LastFixtureStory/CardLayout/index.tsx';
import { LineupRankingDumb } from '#src/layouts/LineupLayout/LineupRanking/Dumb/index.tsx';

import type { LeaderboardDivisionChangeRecap_so5Ranking } from './__generated__/index.graphql.ts';

const divisionEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(2.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;
const iconUpEnter = keyframes`
  0% {
    opacity: 0;
    transform: translateY(50%);
  }
  5% {
    opacity: 1;
    transform: translateY(0%);
  }
  65% {
    opacity: 1;
    transform: translateY(0%);
  }
  70% {
    opacity: 0;
    transform: translateY(-50%);
  }
`;
const iconDownEnter = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-50%);
  }
  5% {
    opacity: 1;
    transform: translateY(0%);
  }
  65% {
    opacity: 1;
    transform: translateY(0%);
  }
  70% {
    opacity: 0;
    transform: translateY(50%);
  }
`;
const PromotionIcon = styled(Vertical).attrs({ gap: 0 })`
  & > *:first-child {
    opacity: 0;
    animation: ${iconUpEnter} 4s ease-out calc(var(--delay) + 1200ms) infinite;
  }
  & > *:nth-child(2) {
    margin-top: calc(-1 * var(--double-unit));
    opacity: 0;
    animation: ${iconUpEnter} 4s ease-out calc(var(--delay) + 1400ms) infinite;
  }
`;
const RelegationIcon = styled(Vertical).attrs({ gap: 0 })`
  & > *:first-child {
    opacity: 0;
    animation: ${iconDownEnter} 4s ease-out calc(var(--delay) + 1400ms) infinite;
  }
  & > *:nth-child(2) {
    margin-top: calc(-1 * var(--double-unit));
    opacity: 0;
    animation: ${iconDownEnter} 4s ease-out calc(var(--delay) + 1200ms) infinite;
  }
`;
const DivisionIconImg = styled.img`
  opacity: 0;
  animation: ${divisionEnter} 0.4s ease-out calc(var(--delay) + 700ms) forwards;
`;
const LineupRanking = styled(LineupRankingDumb)`
  width: 100%;
`;
const LeagueHeader = styled.img`
  width: 100%;
  max-height: calc(5 * var(--unit));
`;

type Props = {
  delay?: number;
  so5Ranking: LeaderboardDivisionChangeRecap_so5Ranking;
};
export const LeaderboardDivisionChangeRecap = ({
  delay = 0,
  so5Ranking,
}: Props) => {
  const { so5Lineup, so5Leaderboard, eligibleDivisionChange, score } =
    so5Ranking;
  const isRelegation = eligibleDivisionChange === DivisionChange.RELEGATED;

  const { so5League } = so5Leaderboard;
  const { managerTeam } = so5Lineup;

  const Icon = isRelegation ? RelegationIcon : PromotionIcon;
  const chevronIcon = isRelegation ? (
    <FontAwesomeIcon
      icon={iconChevronDown}
      color="var(--c-red-400)"
      size="xl"
    />
  ) : (
    <FontAwesomeIcon
      icon={iconChevronUp}
      color="var(--c-score-high)"
      size="xl"
    />
  );

  return (
    <CardLayout
      so5Lineup={so5Lineup}
      bgRgbColor={isRelegation ? '242, 59, 39' : '34, 252, 95'}
      borderColor={isRelegation ? 'var(--c-red-400)' : 'var(--c-score-high)'}
      delay={delay}
      topLeftIcon={
        <Icon>
          {chevronIcon}
          {chevronIcon}
        </Icon>
      }
      icon={
        <DivisionIconImg
          src={managerTeam?.activeDivisionIconUrl}
          alt=""
          width={70}
          height={70}
        />
      }
      subtitle={
        <SeasonalityLabel
          seasonality={so5Leaderboard.seasonality}
          label={so5Leaderboard.seasonalityName}
        />
      }
      title={managerTeam?.name}
      stats={
        <LineupRanking ranking={so5Ranking} score={score} showRankingAndScore />
      }
      footer={<LeagueHeader src={so5League?.headerUrl} alt="" />}
    />
  );
};

LeaderboardDivisionChangeRecap.fragments = {
  so5Ranking: gql`
    fragment LeaderboardDivisionChangeRecap_so5Ranking on So5Ranking {
      id
      ranking
      rankingRatio
      eligibleDivisionChange
      score
      so5Leaderboard {
        slug
        seasonality
        seasonalityName
        so5League {
          slug
          headerUrl
        }
      }
      so5Lineup {
        id
        managerTeam {
          id
          name
          activeDivisionIconUrl
        }
        ...CardLayout_so5lineup
      }
    }
    ${CardLayout.fragments.so5Lineup}
  ` as TypedDocumentNode<LeaderboardDivisionChangeRecap_so5Ranking>,
};
